// The Design Library's API calls (P3-D01, P3-D02; library/router.py), all in one module.
import { infiniteQueryOptions, queryOptions, useInfiniteQuery, type QueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '@/api/client';
import { multipart } from '@/api/multipart';
import { qk } from '@/api/queries';
import type { PresetDetail, PresetOrigin, PresetOut, PresetPage, PresetVersion, SectionTypeOut } from '@/api/types';

/** Cards per server page («Показать ещё» loads the next). */
export const PAGE_SIZE = 48;

export type GalleryFilters = {
  section_type?: string;
  favorites?: boolean;
  search?: string;
};

/** GET /admin/presets: one origin's gallery pages, newest first (a PM gets published presets only). */
export const presetPagesQuery = (filters: GalleryFilters, origin: PresetOrigin) =>
  infiniteQueryOptions({
    queryKey: qk.presets({ ...filters, origin }),
    queryFn: ({ pageParam }): Promise<PresetPage> =>
      unwrap(
        api.GET('/admin/presets', {
          params: {
            query: { ...filters, favorites: filters.favorites || undefined, search: filters.search || undefined, origin: [origin], sort: '-created', page: pageParam, size: PAGE_SIZE },
          },
        }),
      ),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.pages ? last.page + 1 : undefined),
  });

/** How many demo presets the caller sees (the «Демо-пресеты» chip). */
export const demoCountQuery = queryOptions({
  queryKey: qk.presets({ origin: 'demo', count: true }),
  queryFn: async () => (await unwrap(api.GET('/admin/presets', { params: { query: { origin: ['demo'], size: 1 } } }))).total,
});

export type LibraryList = {
  items: PresetOut[];
  total: number;
  loading: boolean;
  error: unknown;
  hasMore: boolean;
  loadingMore: boolean;
  loadMore: () => void;
  retry: () => void;
};

/**
 * The library page's list. «Библиотека» is the authored presets, then the catalog ones, each newest
 * first (two paged queries shown in that order, so a new draft is at the top); «Демо-пресеты» is
 * the design's 233 parametric presets (origin = demo).
 */
export function useLibraryList(filters: GalleryFilters, demo: boolean): LibraryList {
  const authored = useInfiniteQuery({ ...presetPagesQuery(filters, demo ? 'demo' : 'authored') });
  const catalog = useInfiniteQuery({ ...presetPagesQuery(filters, 'catalog'), enabled: !demo });
  const flat = (q: typeof authored) => q.data?.pages.flatMap((p) => p.items) ?? [];
  const totalOf = (q: typeof authored) => q.data?.pages[0]?.total ?? 0;
  if (demo)
    return {
      items: flat(authored),
      total: totalOf(authored),
      loading: authored.isPending,
      error: authored.error,
      hasMore: !!authored.hasNextPage,
      loadingMore: authored.isFetchingNextPage,
      loadMore: () => void authored.fetchNextPage(),
      retry: () => void authored.refetch(),
    };
  // Catalog presets follow only once every authored page is in, so the order never shifts.
  const items = authored.hasNextPage ? flat(authored) : [...flat(authored), ...flat(catalog)];
  return {
    items,
    total: totalOf(authored) + totalOf(catalog),
    loading: authored.isPending || catalog.isPending,
    error: authored.error ?? catalog.error,
    hasMore: !!authored.hasNextPage || !!catalog.hasNextPage,
    loadingMore: authored.isFetchingNextPage || catalog.isFetchingNextPage,
    loadMore: () => void (authored.hasNextPage ? authored.fetchNextPage() : catalog.fetchNextPage()),
    retry: () => {
      void authored.refetch();
      void catalog.refetch();
    },
  };
}

/** GET /admin/section-types: the built-in and custom types with their names and ratios. */
export const sectionTypesQuery = queryOptions({
  queryKey: qk.sectionTypes,
  queryFn: (): Promise<SectionTypeOut[]> => unwrap(api.GET('/admin/section-types')),
  staleTime: 5 * 60_000,
});

/** A build still on its way (the page polls until it is ready or failed). */
export const buildRunning = (status: string | undefined) => !!status && !['ready', 'failed'].includes(status);

/** GET /admin/presets/{id}: the preset with its versions (a PM: published ones). Polls while the
 * latest version's sandbox build runs. */
export const presetQuery = (id: string) =>
  queryOptions({
    queryKey: qk.preset(id),
    queryFn: (): Promise<PresetDetail> => unwrap(api.GET('/admin/presets/{preset_id}', { params: { path: { preset_id: id } } })),
    refetchInterval: (query) => {
      const d = query.state.data;
      const latest = d?.versions.find((v) => v.id === d.latest_version_id);
      return buildRunning(latest?.build?.status) ? 2000 : false;
    },
  });

/** POST /admin/preset-versions/{id}/build: the sandbox build (check → render → QA). */
export function buildVersion(versionId: string): Promise<PresetVersion> {
  return unwrap(api.POST('/admin/preset-versions/{version_id}/build', { params: { path: { version_id: versionId } } }));
}

/** GET /admin/builds/{id}: the build's problems (file, line, rule, hint) when it failed. */
export const buildQuery = (id: string) =>
  queryOptions({
    queryKey: ['build', id],
    queryFn: () => unwrap(api.GET('/admin/builds/{build_id}', { params: { path: { build_id: id } } })),
  });

/** PUT / DELETE /admin/presets/{id}/favorite (per user). */
export async function setFavorite(id: string, on: boolean): Promise<void> {
  const params = { params: { path: { preset_id: id } } };
  await unwrap(on ? api.PUT('/admin/presets/{preset_id}/favorite', params) : api.DELETE('/admin/presets/{preset_id}/favorite', params));
}

/** Flips `is_favorite` on every cached copy of a preset (gallery pages and the detail). */
export function patchCachedPreset(qc: QueryClient, id: string, patch: Partial<PresetOut>): void {
  qc.setQueriesData<{ pages: PresetPage[]; pageParams: unknown[] }>({ queryKey: ['presets'] }, (data) => {
    if (!data || typeof data !== 'object' || !('pages' in data)) return data;
    return { ...data, pages: data.pages.map((p) => ({ ...p, items: p.items.map((x) => (x.id === id ? { ...x, ...patch } : x)) })) };
  });
  qc.setQueryData<PresetDetail>(qk.preset(id), (d) => (d ? { ...d, ...patch } : d));
}

// ── Authoring (P3-D02; Admin) ──

/** POST /admin/section-types: «Нет нужной? Новая структура…» (the name in both languages until it is translated). */
export function addSectionType(name: string): Promise<SectionTypeOut> {
  return unwrap(api.POST('/admin/section-types', { body: { name_ru: name, name_uz: name } }));
}

/** POST /admin/presets/media: one reference photo, scanned and owned by the library. */
export async function uploadReference(file: File): Promise<Schemas['MediaOut']> {
  const { body, contentType } = await multipart({ file });
  return unwrap(
    api.POST('/admin/presets/media', {
      body: { file: '' },
      bodySerializer: () => body as unknown as BodyInit,
      headers: { 'Content-Type': contentType },
    }),
  );
}

/** POST /admin/presets/drafts: one draft (version 1) per section type. */
export function createDrafts(body: Schemas['DraftsCreate']): Promise<PresetDetail[]> {
  return unwrap(api.POST('/admin/presets/drafts', { body }));
}

/** PATCH /admin/presets/{id}: the metadata (name, description, tags, layout, colour mode, mirror). */
export function patchPreset(id: string, body: Schemas['PresetPatch']): Promise<PresetDetail> {
  return unwrap(api.PATCH('/admin/presets/{preset_id}', { params: { path: { preset_id: id } }, body }));
}

/** POST /admin/presets/{id}/versions: new content is a new version (a version never changes). */
export function addVersion(id: string, body: Schemas['PresetContent']): Promise<PresetVersion> {
  return unwrap(api.POST('/admin/presets/{preset_id}/versions', { params: { path: { preset_id: id } }, body }));
}

/** POST /admin/preset-versions/{id}/review: a draft version goes to review. */
export function submitForReview(versionId: string): Promise<PresetVersion> {
  return unwrap(api.POST('/admin/preset-versions/{version_id}/review', { params: { path: { version_id: versionId } } }));
}

/** POST /admin/preset-versions/{id}/publish (`preset:publish`): the preset becomes published. */
export function publishVersion(versionId: string): Promise<PresetVersion> {
  return unwrap(api.POST('/admin/preset-versions/{version_id}/publish', { params: { path: { version_id: versionId } } }));
}

export function archivePreset(id: string): Promise<PresetOut> {
  return unwrap(api.POST('/admin/presets/{preset_id}/archive', { params: { path: { preset_id: id } } }));
}

export function restorePreset(id: string): Promise<PresetOut> {
  return unwrap(api.POST('/admin/presets/{preset_id}/restore', { params: { path: { preset_id: id } } }));
}
